import { createSlice, isAnyOf } from '@reduxjs/toolkit'
import {
  getAllRuleListAsync,
  getGetEditDataAsync,
  getEventListAsync,
  getTriggerListAsync,
  getConditionListAsync,
  getActionListAsync,
  createRuleAsync,
  updateRuleAsync,
  deleteRuleAsync,
  getAllSecondaryPlatformListAsync,
  getAllNonSecondaryPlatformListAsync,
  getAllInventoryLocationPlatformListAsync,
  getSelectMultiselectByActionCodeAsync,
  getConditionCodeDataAsync,
} from '../actions/ruleBuilderAction'

type ActionCodeOptions = {
  [actionCode: string]: Array<{
    option_id: number | string;
    display_label: string;
    platform_field_id?: number | null;
    platform_field_data_id?: number | string | null;
    value?: number | string;
    child_fields?: any[];
  }>;
};

const extractApiResults = (payloadData: unknown): any[] => {
  if (Array.isArray(payloadData)) return payloadData
  if (payloadData && typeof payloadData === 'object' && Array.isArray((payloadData as { results?: unknown }).results)) {
    return (payloadData as { results: any[] }).results
  }
  return []
}

const initialState = {
  // Lists
  ruleList: [] as any[],
  ruleListTotalCount: 0,
  eventList: [],
  triggerList: [],
  conditionList: [],
  actionList: [],
  allSecondaryList: [],
  allNonSecondaryList: [],
  allInventoryLocationList: [],
  selectMultiselectOptionsByActionCode: {},
  conditionCodeOptionsByKey: {},
  actionCodeHasMore: {},
  conditionCodeHasMore: {},

  // Edit
  editRuleData: null,

  // Loading flags
  loading: {
    ruleList: false,
    editRule: false,
    eventList: false,
    triggerList: false,
    conditionList: false,
    actionList: false,
    saveRule: false,
    deleteRule: false,
    allSecondaryList: false,
    allNonSecondaryList: false,
    allInventoryLocationList: false,
    selectMultiselectByActionCode: false,
    conditionCodeData: false,
  },

  // Responses
  saveRuleResponse: null,
}

const ruleBuilderSlice = createSlice({
  name: 'ruleBuilderSlice',
  initialState,
  reducers: {
    clearRuleBuilderState: () => initialState,
  },
  extraReducers: (builder) => {
    /* ---------------- All Rule List ---------------- */
    builder
      .addCase(getAllRuleListAsync.pending, (state) => {
        state.loading.ruleList = true
      })
      .addCase(getAllRuleListAsync.fulfilled, (state, action) => {
        state.loading.ruleList = false
        const payloadData = action?.payload?.data;
        if (payloadData && typeof payloadData === 'object' && 'data' in payloadData && Array.isArray(payloadData.data)) {
            state.ruleList = payloadData.data;
            state.ruleListTotalCount = payloadData.total || 0;
        } else {
            state.ruleList = extractApiResults(payloadData);
            state.ruleListTotalCount = payloadData?.total || payloadData?.count || payloadData?.totalCount || 0;
        }
      })
      .addCase(getAllRuleListAsync.rejected, (state) => {
        state.loading.ruleList = false
      })

    /* ---------------- Edit Rule ---------------- */
    builder
      .addCase(getGetEditDataAsync.pending, (state) => {
        state.loading.editRule = true
        state.editRuleData = null
      })
      .addCase(getGetEditDataAsync.fulfilled, (state, action) => {
        state.loading.editRule = false
        state.editRuleData = action?.payload?.data
      })
      .addCase(getGetEditDataAsync.rejected, (state) => {
        state.loading.editRule = false
      })

    /* ---------------- Events ---------------- */
    builder
      .addCase(getEventListAsync.pending, (state) => {
        state.loading.eventList = true
      })
      .addCase(getEventListAsync.fulfilled, (state, action) => {
        state.loading.eventList = false
        state.eventList = action?.payload?.data
      })
      .addCase(getEventListAsync.rejected, (state) => {
        state.loading.eventList = false
      })

    /* ---------------- Triggers ---------------- */
    builder
      .addCase(getTriggerListAsync.pending, (state) => {
        state.loading.triggerList = true
      })
      .addCase(getTriggerListAsync.fulfilled, (state, action) => {
        state.loading.triggerList = false
        state.triggerList = action?.payload?.data
      })
      .addCase(getTriggerListAsync.rejected, (state) => {
        state.loading.triggerList = false
      })

    /* ---------------- Conditions ---------------- */
    builder
      .addCase(getConditionListAsync.pending, (state) => {
        state.loading.conditionList = true
      })
      .addCase(getConditionListAsync.fulfilled, (state, action) => {
        state.loading.conditionList = false
        state.conditionList = action?.payload?.data
      })
      .addCase(getConditionListAsync.rejected, (state) => {
        state.loading.conditionList = false
      })

    /* ---------------- Actions ---------------- */
    builder
      .addCase(getActionListAsync.pending, (state) => {
        state.loading.actionList = true
      })
      .addCase(getActionListAsync.fulfilled, (state, action) => {
        state.loading.actionList = false
        state.actionList = action.payload?.data
      })
      .addCase(getActionListAsync.rejected, (state) => {
        state.loading.actionList = false
      })


    /* ---------------- All Secondary ---------------- */
    builder
      .addCase(getAllSecondaryPlatformListAsync.pending, (state) => {
        state.loading.allSecondaryList = true;
      })
      .addCase(getAllSecondaryPlatformListAsync.fulfilled, (state, action) => {
        state.loading.allSecondaryList = false;
        state.allSecondaryList = action.payload?.data;
      })
      .addCase(getAllSecondaryPlatformListAsync.rejected, (state, action) => {
        state.loading.allSecondaryList = false;
        console.error('failed to fetch all secondary:', action.payload);
      });

    /* ---------------- All Non-Secondary ---------------- */
    builder
      .addCase(getAllNonSecondaryPlatformListAsync.pending, (state) => {
        state.loading.allNonSecondaryList = true;
      })
      .addCase(getAllNonSecondaryPlatformListAsync.fulfilled, (state, action) => {
        state.loading.allNonSecondaryList = false;
        state.allNonSecondaryList = action.payload?.data;
      })
      .addCase(getAllNonSecondaryPlatformListAsync.rejected, (state, action) => {
        state.loading.allNonSecondaryList = false;
        console.error('failed to fetch all non-secondary:', action.payload);
      });

    /* ---------------- All Inventory Locations ---------------- */
    builder
      .addCase(getAllInventoryLocationPlatformListAsync.pending, (state) => {
        state.loading.allInventoryLocationList = true;
      })
      .addCase(getAllInventoryLocationPlatformListAsync.fulfilled, (state, action) => {
        state.loading.allInventoryLocationList = false;
        state.allInventoryLocationList = action.payload?.data ?? [];
      })
      .addCase(getAllInventoryLocationPlatformListAsync.rejected, (state, action) => {
        state.loading.allInventoryLocationList = false;
        console.error('failed to fetch inventory locations:', action.payload);
      });

          /* ---------------- Select / Multiselect by Action Code ---------------- */
    builder
      .addCase(getSelectMultiselectByActionCodeAsync.pending, (state) => {
        state.loading.selectMultiselectByActionCode = true
      })
      .addCase(getSelectMultiselectByActionCodeAsync.fulfilled, (state, action) => {
        state.loading.selectMultiselectByActionCode = false

        const actionCode = action?.meta?.arg?.actionCode
        const fieldCode = action?.meta?.arg?.fieldCode ?? ''
        const selectedValue = action?.meta?.arg?.selectedValue ?? ''
        const page = action?.meta?.arg?.page ?? 1
        const limit = action?.meta?.arg?.limit ?? 10
        const key = fieldCode ? `${actionCode}:${fieldCode}:${selectedValue}` : actionCode
        const apiData = extractApiResults(action?.payload?.data)
        const incoming = apiData.map((item: any) => ({
          option_id: item.id ?? item.platform_field_data_id ?? item.value,
          display_label: item.display_name || item.api_name || item.name || '',
          platform_field_id: item.platform_field_id ?? item.platform_field?.id ?? item.platform_field ?? null,
          platform_field_data_id: item.platform_field_data_id ?? item.id ?? null,
          value: item.value ?? item.id ?? item.platform_field_data_id ?? '',
          child_fields: item.child_fields ?? [],
        }))

        if (page > 1) {
          state.selectMultiselectOptionsByActionCode[key] = [
            ...(state.selectMultiselectOptionsByActionCode[key] ?? []),
            ...incoming,
          ]
        } else {
          state.selectMultiselectOptionsByActionCode[key] = incoming
        }
        state.actionCodeHasMore[key] = apiData.length === limit
      })
      .addCase(getSelectMultiselectByActionCodeAsync.rejected, (state, action) => {
        state.loading.selectMultiselectByActionCode = false
        console.error('failed to fetch select/multiselect options by action code:', action.payload)
      })

    /* ---------------- Condition code data ---------------- */
    builder
      .addCase(getConditionCodeDataAsync.pending, (state) => {
        state.loading.conditionCodeData = true
      })
      .addCase(getConditionCodeDataAsync.fulfilled, (state, action) => {
        state.loading.conditionCodeData = false

        const fieldCode = action?.meta?.arg?.fieldCode
        const conditionCode = action?.meta?.arg?.conditionCode ?? ''
        const selectedValue = action?.meta?.arg?.selectedValue ?? ''
        const page = action?.meta?.arg?.page ?? 1
        const limit = action?.meta?.arg?.limit ?? 10
        const key = `${conditionCode}:${fieldCode}:${selectedValue}`
        const apiData = extractApiResults(action?.payload?.data)
        const incoming = apiData.map((item: any) => ({
          option_id: item.id ?? item.platform_field_data_id ?? item.value,
          display_label: item.display_name || item.api_name || item.name || item.label || String(item.id ?? ''),
          platform_field_id: item.platform_field_id ?? item.platform_field?.id ?? item.platform_field ?? null,
          platform_field_data_id: item.platform_field_data_id ?? item.id ?? null,
          value: item.value ?? item.id ?? item.platform_field_data_id ?? '',
          child_fields: item.child_fields ?? [],
        }))

        if (page > 1) {
          state.conditionCodeOptionsByKey[key] = [
            ...(state.conditionCodeOptionsByKey[key] ?? []),
            ...incoming,
          ]
        } else {
          state.conditionCodeOptionsByKey[key] = incoming
        }
        state.conditionCodeHasMore[key] = apiData.length === limit
      })
      .addCase(getConditionCodeDataAsync.rejected, (state, action) => {
        state.loading.conditionCodeData = false
        console.error('failed to fetch condition code data:', action.payload)
      })

    /* ---------------- Delete Rule ---------------- */
    builder
      .addCase(deleteRuleAsync.pending, (state) => {
        state.loading.deleteRule = true
      })
      .addCase(deleteRuleAsync.fulfilled, (state) => {
        state.loading.deleteRule = false
      })
      .addCase(deleteRuleAsync.rejected, (state) => {
        state.loading.deleteRule = false
      })
    /* ---------------- Create / Update Rule ---------------- */
    builder
      .addMatcher(
        isAnyOf(createRuleAsync.pending, updateRuleAsync.pending),
        (state) => {
          state.loading.saveRule = true
        }
      )
      .addMatcher(
        isAnyOf(createRuleAsync.fulfilled, updateRuleAsync.fulfilled),
        (state, action) => {
          state.loading.saveRule = false
          state.saveRuleResponse = action.payload
        }
      )
      .addMatcher(
        isAnyOf(createRuleAsync.rejected, updateRuleAsync.rejected),
        (state) => {
          state.loading.saveRule = false
        }
      )

  },
})

export const { clearRuleBuilderState } = ruleBuilderSlice.actions
export default ruleBuilderSlice.reducer
